import React, { useCallback, useMemo, useState } from "react";
// eslint-disable-next-line no-restricted-imports
import { useLazyLoadQuery } from "react-relay";

import { Badge, Button, Tabs } from "@skydio/rivet-ui";

import SuspenseAndErrorBoundary from "components/common/SuspenseAndErrorBoundary";
import {
  VehicleTypeCategoriesContext,
  vehicleTypeCategoriesQuery,
} from "components/common/VehicleTypeCategories";
import SettingsPageView from "components/main/settings/SettingsPageView";
import { useQueryStringState } from "hooks/useQueryStringState";

import { AttachmentsPage, AttachmentsPageContext } from "./components/AttachmentsList";
import { BatteriesPage, BatteriesPageContext } from "./components/BatteriesList";
import { ControllersPage, ControllersPageContext } from "./components/ControllersList";
import { DocksPage, DocksPageContext } from "./components/DocksList";
import ExternalRadioPage, { ExternalRadioPageContext } from "./components/ExternalRadioList";
import { FlightsPage, FlightsPageContext } from "./components/FlightsList";
import { OrganizationsPage, OrganizationsPageContext } from "./components/OrganizationsPage";
import { SensorsPage, SensorsPageContext } from "./components/SensorsList";
import { UsersPage, UsersPageContext } from "./components/UsersList";
import { VehiclesPage, VehiclesPageContext } from "./components/VehiclesList";

import styles from "./AdminSearchPage.module.scss";

import type { Key } from "@skydio/rivet-ui";
import type { VehicleTypeCategoriesQuery } from "__generated__/VehicleTypeCategoriesQuery.graphql";

const AdminSearchPage = () => {
  const [selectedTab, setSelectedTab] = useQueryStringState<Key>("tab", "o");
  const [orgsCount, setOrgsCount] = useState<number | undefined>(undefined);
  const [vehiclesCount, setVehiclesCount] = useState<number | undefined>(undefined);
  const [docksCount, setDocksCount] = useState<number | undefined>(undefined);
  const [externalRadioCount, setExternalRadioCount] = useState<number | undefined>(undefined);
  const [controllersCount, setControllersCount] = useState<number | undefined>(undefined);
  const [flightsCount, setFlightsCount] = useState<number | undefined>(undefined);
  const [batteriesCount, setBatteriesCount] = useState<number | undefined>(undefined);
  const [usersCount, setUsersCount] = useState<number | undefined>(undefined);
  const [attachmentsCount, setAttachmentsCount] = useState<number | undefined>(undefined);
  const [sensorsCount, setSensorsCount] = useState<number | undefined>(undefined);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);

  const vehicleTypeCategories = useLazyLoadQuery<VehicleTypeCategoriesQuery>(
    vehicleTypeCategoriesQuery,
    {}
  );
  const vehicleTypeCategoriesContextValue = useMemo(() => {
    return { categories: vehicleTypeCategories };
  }, [vehicleTypeCategories]);

  const docksPageContextValue = useMemo(() => {
    return { setCount: setDocksCount };
  }, []);
  const flightsPageContextValue = useMemo(() => {
    return { setCount: setFlightsCount };
  }, []);
  const batteriesPageContextValue = useMemo(() => {
    return { setCount: setBatteriesCount };
  }, []);
  const vehiclesPageContextValue = useMemo(() => {
    return { setCount: setVehiclesCount };
  }, []);
  const externalRadioPageContextValue = useMemo(() => {
    return { setCount: setExternalRadioCount };
  }, []);
  const usersPageContextValue = useMemo(() => {
    return { setCount: setUsersCount };
  }, []);
  const sensorsPageContextValue = useMemo(() => {
    return { setCount: setSensorsCount };
  }, []);
  const organizationsPageContextValue = useMemo(() => {
    return {
      isCreateModalVisible,
      setIsCreateModalVisible,
      setCount: setOrgsCount,
    };
  }, [isCreateModalVisible]);
  const controllersPageContextValue = useMemo(() => {
    return { setCount: setControllersCount };
  }, []);
  const attachmentsPageContextValue = useMemo(() => {
    return { setCount: setAttachmentsCount };
  }, []);

  const createOrgButtonOnClick = useCallback(() => {
    setIsCreateModalVisible(true);
  }, []);

  const settingsPageViewExtra = useMemo(() => {
    return (
      <div className="flex gap-x-2">
        <Button variant="primary" onClick={createOrgButtonOnClick}>
          Create Organization
        </Button>
      </div>
    );
  }, [createOrgButtonOnClick]);

  const orgTitle = useMemo(() => {
    return (
      <Badge content={orgsCount && orgsCount > 99 ? "99+" : orgsCount} isInvisible={!orgsCount}>
        Organizations
      </Badge>
    );
  }, [orgsCount]);

  const vehiclesTitle = useMemo(() => {
    return (
      <Badge
        content={vehiclesCount && vehiclesCount > 99 ? "99+" : vehiclesCount}
        isInvisible={!vehiclesCount}
      >
        Vehicles
      </Badge>
    );
  }, [vehiclesCount]);

  const docksTitle = useMemo(() => {
    return (
      <Badge content={docksCount && docksCount > 99 ? "99+" : docksCount} isInvisible={!docksCount}>
        Docks
      </Badge>
    );
  }, [docksCount]);

  const externalRadiosTitle = useMemo(() => {
    return (
      <div className="flex gap-x-2">
        <Badge
          content={externalRadioCount && externalRadioCount > 99 ? "99+" : externalRadioCount}
          isInvisible={!externalRadioCount}
        >
          External Radios
        </Badge>
      </div>
    );
  }, [externalRadioCount]);
  const controllersTitle = useMemo(() => {
    return (
      <Badge
        content={controllersCount && controllersCount > 99 ? "99+" : controllersCount}
        isInvisible={!controllersCount}
      >
        Controllers
      </Badge>
    );
  }, [controllersCount]);
  const flightsTitle = useMemo(() => {
    return (
      <Badge
        content={flightsCount && flightsCount > 99 ? "99+" : flightsCount}
        isInvisible={!flightsCount}
      >
        Flights
      </Badge>
    );
  }, [flightsCount]);
  const batteriesTitle = useMemo(() => {
    return (
      <Badge
        content={batteriesCount && batteriesCount > 99 ? "99+" : batteriesCount}
        isInvisible={!batteriesCount}
      >
        Batteries
      </Badge>
    );
  }, [batteriesCount]);
  const usersTitle = useMemo(() => {
    return (
      <Badge content={usersCount && usersCount > 99 ? "99+" : usersCount} isInvisible={!usersCount}>
        Users
      </Badge>
    );
  }, [usersCount]);

  const sensorsTitle = useMemo(() => {
    return (
      <Badge
        content={sensorsCount && sensorsCount > 99 ? "99+" : sensorsCount}
        isInvisible={!sensorsCount}
      >
        Sensors
      </Badge>
    );
  }, [sensorsCount]);

  const attachmentsTitle = useMemo(() => {
    return (
      <Badge
        content={attachmentsCount && attachmentsCount > 99 ? "99+" : attachmentsCount}
        isInvisible={!attachmentsCount}
      >
        Attachments
      </Badge>
    );
  }, [attachmentsCount]);

  return (
    <SettingsPageView title="Admin Search" extra={settingsPageViewExtra}>
      <Tabs
        selectedKey={selectedTab}
        onSelectionChange={setSelectedTab}
        className={styles.tabs}
        variant="underlined"
        destroyInactiveTabPanel={false}
      >
        <Tabs.Tab key="o" title={orgTitle}>
          <SuspenseAndErrorBoundary>
            <OrganizationsPageContext.Provider value={organizationsPageContextValue}>
              <OrganizationsPage />
            </OrganizationsPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="v" title={vehiclesTitle}>
          <SuspenseAndErrorBoundary>
            <VehicleTypeCategoriesContext.Provider value={vehicleTypeCategoriesContextValue}>
              <VehiclesPageContext.Provider value={vehiclesPageContextValue}>
                <VehiclesPage />
              </VehiclesPageContext.Provider>
            </VehicleTypeCategoriesContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="d" title={docksTitle}>
          <SuspenseAndErrorBoundary>
            <DocksPageContext.Provider value={docksPageContextValue}>
              <DocksPage />
            </DocksPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="a" title={externalRadiosTitle}>
          <SuspenseAndErrorBoundary>
            <VehicleTypeCategoriesContext.Provider value={vehicleTypeCategoriesContextValue}>
              <ExternalRadioPageContext.Provider value={externalRadioPageContextValue}>
                <ExternalRadioPage />
              </ExternalRadioPageContext.Provider>
            </VehicleTypeCategoriesContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="c" title={controllersTitle}>
          <SuspenseAndErrorBoundary>
            <ControllersPageContext.Provider value={controllersPageContextValue}>
              <ControllersPage />
            </ControllersPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="f" title={flightsTitle}>
          <SuspenseAndErrorBoundary>
            <FlightsPageContext.Provider value={flightsPageContextValue}>
              <FlightsPage />
            </FlightsPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="b" title={batteriesTitle}>
          <SuspenseAndErrorBoundary>
            <BatteriesPageContext.Provider value={batteriesPageContextValue}>
              <BatteriesPage />
            </BatteriesPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="u" title={usersTitle}>
          <SuspenseAndErrorBoundary>
            <UsersPageContext.Provider value={usersPageContextValue}>
              <UsersPage />
            </UsersPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="s" title={sensorsTitle}>
          <SuspenseAndErrorBoundary>
            <SensorsPageContext.Provider value={sensorsPageContextValue}>
              <SensorsPage />
            </SensorsPageContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
        <Tabs.Tab key="at" title={attachmentsTitle}>
          <SuspenseAndErrorBoundary>
            <VehicleTypeCategoriesContext.Provider value={vehicleTypeCategoriesContextValue}>
              <AttachmentsPageContext.Provider value={attachmentsPageContextValue}>
                <AttachmentsPage />
              </AttachmentsPageContext.Provider>
            </VehicleTypeCategoriesContext.Provider>
          </SuspenseAndErrorBoundary>
        </Tabs.Tab>
      </Tabs>
    </SettingsPageView>
  );
};

export default AdminSearchPage;
